import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, ChevronRight, Plus, Trash2 } from 'lucide-react'
import MatchCard from './MatchCard'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'

function formatPlayerPreview(player, isTeam) {
  if (isTeam) {
    if (!Array.isArray(player) || player.length === 0) return '—'
    const names = player.map((p) => p?.name).filter(Boolean)
    if (names.length === 0) return '—'
    return names.join(' / ')
  }
  return player?.name || '—'
}

function OutlineRow({
  match,
  isTeam,
  isSelected,
  hasErrors,
  onSelect,
  onDelete,
  onInsertBelow,
}) {
  const isPQ = match.round === 0

  let badgeVariant = 'blue'
  let label = `M${match.matchNumber}`
  if (isPQ) {
    badgeVariant =
      match.feedsIntoSlot === 1 ? 'teal' : match.feedsIntoSlot === 2 ? 'orange' : 'muted'
    label = `PQ-${match.matchNumber}`
  } else if (match.round > 1) {
    label = `R${match.round}M${match.matchNumber}`
  }

  const seed1 = match.player1PQRef
    ? match.player1PQRef
    : match.player1Seed
      ? `#${match.player1Seed}`
      : '#—'
  const seed2 = match.player2PQRef
    ? match.player2PQRef
    : match.player2Seed
      ? `#${match.player2Seed}`
      : '#—'
  const name1 = match.player1PQRef
    ? 'Winner of PQ'
    : formatPlayerPreview(match.player1, isTeam)
  const name2 = match.player2PQRef
    ? 'Winner of PQ'
    : formatPlayerPreview(match.player2, isTeam)

  return (
    <li>
      <div
        className={`group relative flex items-center gap-2 rounded-md border px-2 py-2 text-sm transition-colors cursor-pointer ${
          isSelected
            ? 'border-primary bg-primary/5'
            : 'border-transparent hover:bg-muted/50'
        } ${isPQ ? 'ml-6' : ''}`}
        onClick={onSelect}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }}
        data-outline-row-id={match.id}
      >
        <Badge variant={badgeVariant} className="shrink-0 text-[10px]">
          {label}
        </Badge>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 truncate">
            <span className="text-muted-foreground text-xs shrink-0">{seed1}</span>
            <span className="truncate">{name1}</span>
          </div>
          <div className="flex items-center gap-1 truncate">
            <span className="text-muted-foreground text-xs shrink-0">{seed2}</span>
            <span className="truncate">{name2}</span>
          </div>
        </div>
        {hasErrors && (
          <AlertCircle
            className="h-4 w-4 shrink-0 text-destructive"
            aria-label="Has validation errors"
          />
        )}
        <ChevronRight
          className={`h-4 w-4 shrink-0 transition-opacity ${
            isSelected ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-40'
          }`}
        />
        <div className="absolute right-1 top-1 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 bg-background/90 rounded">
          {onInsertBelow && !isPQ && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-primary"
              onClick={(e) => {
                e.stopPropagation()
                onInsertBelow()
              }}
              tabIndex={-1}
              title="Insert match below"
              aria-label="Insert match below"
            >
              <Plus className="h-3 w-3" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            tabIndex={-1}
            title="Delete match"
            aria-label="Delete match"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </li>
  )
}

export default function BracketOutlineEditor({
  matches,
  bracket,
  matchRefs,
  displayValidationErrors,
  onAddMatch,
  onInsertMatch,
  onAddPQMatch,
  onDeleteMatch,
  onUpdateMatch,
  onPlayer1TeamChange,
  onPlayer2TeamChange,
  onFocusCapture,
  onBlurCapture,
  getUsedPlayerIds,
}) {
  const isTeam = bracket?.divisionTeamSize > 1
  const [selectedId, setSelectedId] = useState(() => matches[0]?.id ?? null)
  const outlineRef = useRef(null)

  // Keep selection valid as matches are added/deleted/renumbered
  useEffect(() => {
    if (matches.length === 0) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    if (!matches.find((m) => m.id === selectedId)) {
      setSelectedId(matches[0].id)
    }
  }, [matches, selectedId])

  // Auto-scroll the outline row into view when selection changes
  useEffect(() => {
    if (!selectedId || !outlineRef.current) return
    const row = outlineRef.current.querySelector(
      `[data-outline-row-id="${selectedId}"]`
    )
    if (row) {
      const rect = row.getBoundingClientRect()
      const listRect = outlineRef.current.getBoundingClientRect()
      if (rect.top < listRect.top || rect.bottom > listRect.bottom) {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedId])

  const errorMatchIds = useMemo(() => {
    const ids = new Set()
    Object.keys(displayValidationErrors || {}).forEach((key) => {
      // keys look like `match-<id>-p1-seed`
      const prefix = 'match-'
      if (!key.startsWith(prefix)) return
      const rest = key.slice(prefix.length)
      // matchId can be numeric or uuid; strip from first `-p` onwards
      const idx = rest.indexOf('-p')
      const id = idx === -1 ? rest : rest.slice(0, idx)
      ids.add(id)
    })
    return ids
  }, [displayValidationErrors])

  const selectedMatch = selectedId
    ? matches.find((m) => m.id === selectedId)
    : null
  const selectedIndex = selectedMatch
    ? matches.findIndex((m) => m.id === selectedId)
    : -1

  const moveSelection = (delta) => {
    if (matches.length === 0) return
    const next =
      selectedIndex === -1
        ? matches[0]
        : matches[Math.max(0, Math.min(matches.length - 1, selectedIndex + delta))]
    if (next && next.id !== selectedId) setSelectedId(next.id)
  }

  const handleContainerKeyDown = (e) => {
    const target = e.target
    const isEditable =
      target instanceof HTMLElement &&
      (target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable)
    if (isEditable) return
    if (e.key === 'ArrowDown' || e.key === 'j') {
      e.preventDefault()
      moveSelection(1)
    } else if (e.key === 'ArrowUp' || e.key === 'k') {
      e.preventDefault()
      moveSelection(-1)
    }
  }

  // Map selected match's validation errors for MatchCard
  const usedPlayerIds = getUsedPlayerIds()

  return (
    <div
      className="md:flex md:gap-6"
      onKeyDown={handleContainerKeyDown}
      role="region"
      aria-label="Bracket outline editor"
    >
      {/* Outline pane */}
      <div className="md:w-80 md:shrink-0">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            Matches ({matches.length})
          </span>
          <span className="hidden md:inline text-xs text-muted-foreground">
            ↑/↓ or j/k
          </span>
        </div>
        <div
          ref={outlineRef}
          className="md:max-h-[calc(100vh-220px)] md:overflow-y-auto pr-1"
        >
          {matches.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              No matches yet. Add your first one below.
            </div>
          ) : (
            <ul className="space-y-1">
              {matches.map((match) => (
                <OutlineRow
                  key={match.id}
                  match={match}
                  isTeam={isTeam}
                  isSelected={match.id === selectedId}
                  hasErrors={errorMatchIds.has(String(match.id))}
                  onSelect={() => setSelectedId(match.id)}
                  onDelete={() => onDeleteMatch(match.id)}
                  onInsertBelow={
                    match.round === 1 && onInsertMatch
                      ? () => onInsertMatch(match.id)
                      : null
                  }
                />
              ))}
            </ul>
          )}
          <Button
            onClick={onAddMatch}
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            data-add-match-button
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Match
          </Button>
        </div>
      </div>

      {/* Editor pane */}
      <div
        className="flex-1 mt-6 md:mt-0"
        onFocusCapture={onFocusCapture}
        onBlurCapture={onBlurCapture}
      >
        {selectedMatch ? (
          <div
            key={selectedMatch.id}
            data-match-container="true"
            data-match-id={selectedMatch.id}
          >
            <MatchCard
              ref={(el) => (matchRefs.current[selectedMatch.id] = el)}
              matchNumber={selectedMatch.matchNumber}
              round={selectedMatch.round}
              player1={selectedMatch.player1}
              player1Seed={selectedMatch.player1Seed}
              player2={selectedMatch.player2}
              player2Seed={selectedMatch.player2Seed}
              player1PQRef={selectedMatch.player1PQRef}
              player2PQRef={selectedMatch.player2PQRef}
              nextRound={selectedMatch.nextRound}
              nextMatchNumber={selectedMatch.nextMatchNumber}
              onPlayer1Change={(value) =>
                onUpdateMatch(selectedMatch.id, 'player1', value)
              }
              onPlayer1SeedChange={(value) =>
                onUpdateMatch(selectedMatch.id, 'player1Seed', value)
              }
              onPlayer2Change={(value) =>
                onUpdateMatch(selectedMatch.id, 'player2', value)
              }
              onPlayer2SeedChange={(value) =>
                onUpdateMatch(selectedMatch.id, 'player2Seed', value)
              }
              onPlayer1TeamChange={(index, value) =>
                onPlayer1TeamChange(selectedMatch.id, index, value)
              }
              onPlayer2TeamChange={(index, value) =>
                onPlayer2TeamChange(selectedMatch.id, index, value)
              }
              onDelete={() => onDeleteMatch(selectedMatch.id)}
              onInsertBelow={
                selectedMatch.round === 1 && onInsertMatch
                  ? () => onInsertMatch(selectedMatch.id)
                  : null
              }
              onAddPQ={(slot) => onAddPQMatch(selectedMatch.id, slot)}
              showDelete={true}
              divisionGender={bracket?.divisionGender}
              divisionTeamSize={bracket?.divisionTeamSize}
              sportId={bracket?.sportId}
              validationErrors={displayValidationErrors}
              matchKey={`match-${selectedMatch.id}`}
              excludePlayerIds={usedPlayerIds}
              feedsIntoSlot={selectedMatch.feedsIntoSlot}
              parentMatchSeedTabIndex={null}
            />
          </div>
        ) : (
          <div className="rounded-lg border border-dashed p-12 text-center text-muted-foreground">
            Select a match on the left to edit it, or add a new match.
          </div>
        )}
      </div>
    </div>
  )
}
